import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/cn";

const spinnerVariants = cva("animate-spin", {
  variants: {
    size: {
      sm: "size-4",
      md: "size-6",
      default: "size-6",
      lg: "size-8",
      xl: "size-12",
    },
    speed: {
      slow: "[animation-duration:1200ms]",
      default: "[animation-duration:900ms]",
      fast: "[animation-duration:600ms]",
    },
  },
  defaultVariants: {
    size: "lg",
    speed: "fast",
  },
});

export interface SpinnerProps
  extends Omit<React.ComponentProps<"svg">, "size" | "speed">, VariantProps<typeof spinnerVariants> {
  /**
   * Stroke width of the spinner arc
   * @default 3
   */
  strokeWidth?: number;
  /**
   * CSS class for the background circle
   * @default "stroke-muted-foreground/20"
   */
  bgClassName?: string;
  wrapperClassName?: string;
  spinnerClassName?: string;
}

const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  (
    { size, speed, strokeWidth = 3, className, wrapperClassName, bgClassName = "stroke-muted-foreground/20", ...props },
    ref,
  ) => {
    return (
      <div role="status" className={cn("size-full grid place-items-center", wrapperClassName)}>
        <svg
          ref={ref}
          className={cn(spinnerVariants({ size, speed }), "text-primary", className)}
          viewBox="0 0 42 42"
          {...props}
        >
          <g fill="none" transform="translate(3 3)" strokeWidth={strokeWidth}>
            <circle className={bgClassName} cx="18" cy="18" r="18" />
            <path
              className="stroke-current"
              stroke="currentColor"
              d="M36 18c0-9.94-8.06-18-18-18"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      </div>
    );
  },
);

Spinner.displayName = "Spinner";

/* eslint-disable-next-line */
export { Spinner, spinnerVariants };
